import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MailCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        heading="Check your email"
        sub="If that account exists, a link to choose a new password is on its way. It's good for one hour."
      >
        <div className="rounded-xl border border-line bg-surface px-4 py-3.5 flex items-start gap-3">
          <MailCheck className="w-[18px] h-[18px] text-brand shrink-0 mt-0.5" />
          <p className="text-[13px] text-muted2 leading-relaxed">
            Nothing arrived? Check spam, or ask whoever set up your account to send
            you a reset link directly.
          </p>
        </div>
        <Link to="/login" className="link-quiet text-[13px] font-medium mt-6 inline-block">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      heading="Forgotten your password?"
      sub="Enter your email and we'll send a link to choose a new one."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
      <Link to="/login" className="link-quiet text-[13px] font-medium mt-6 inline-block">
        Back to sign in
      </Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!token) {
      setValid(false);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/reset-check", { params: { token } });
      setValid(!!data.valid);
    } catch {
      setValid(false);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    check();
  }, [check]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("The two passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.new_password });
      toast.success("Password changed — sign in with it now");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <AuthShell heading="One moment" sub="Checking that link…">
        <Loader2 className="w-5 h-5 animate-spin text-brand" />
      </AuthShell>
    );
  }

  if (!valid) {
    return (
      <AuthShell
        heading="That link doesn't work"
        sub="Reset links last an hour and can only be used once."
      >
        <Button asChild className="bg-brand hover:bg-brand-hover">
          <Link to="/forgot-password">Ask for a new one</Link>
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell heading="Choose a new password" sub="You'll sign in with this from now on.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="np">New password</Label>
          <PasswordInput
            id="np"
            required
            autoComplete="new-password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          />
          <p className="text-[12px] text-muted2">At least 8 characters.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cp">Confirm password</Label>
          <PasswordInput
            id="cp"
            required
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Change password
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
